<template>
  <v-app>

    <!-- ═══════════════════════════════════════════════
         TOP APP BAR
    ═══════════════════════════════════════════════ -->
    <v-app-bar elevation="0" color="transparent" height="64" class="app-bar">

      <!-- Botão hamburguer -->
      <v-app-bar-nav-icon
        @click="toggleDrawer"
        aria-label="Abrir/fechar menu de navegação"
        color="white"
      />

      <!-- Logo + Título -->
      <div class="app-brand">
        <v-icon color="white" size="22">mdi-chart-bar</v-icon>
        <span class="app-brand-text">TABOSA RELATÓRIOS</span>
      </div>

      <v-spacer />

      <!-- Notificações -->
      <v-btn icon color="white" class="me-1" aria-label="Notificações">
        <v-badge
          :model-value="notificationCount > 0"
          :content="notificationCount"
          color="error"
        >
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>

      <!-- Menu do Usuário -->
      <v-menu :close-on-content-click="true" offset="8">
        <template #activator="{ props }">
          <v-btn icon v-bind="props" aria-label="Menu do usuário">
            <v-avatar size="34" class="user-avatar">
              <span class="user-initials">{{ currentUser.initials }}</span>
            </v-avatar>
          </v-btn>
        </template>

        <v-card width="230" rounded="lg" elevation="4">

          <!-- Cabeçalho com dados do usuário -->
          <div class="user-menu-header">
            <v-avatar size="42" color="primary" class="mb-2">
              <span class="text-white font-weight-bold text-body-1">{{ currentUser.initials }}</span>
            </v-avatar>
            <div class="text-body-2 font-weight-bold text-heading">{{ currentUser.name }}</div>
            <div class="text-caption text-muted">{{ currentUser.role }}</div>
            <div class="text-caption text-muted">{{ currentUser.email }}</div>
          </div>

          <v-divider />

          <v-list density="compact" nav class="py-1">
            <v-list-item prepend-icon="mdi-account-outline" title="Meu Perfil" rounded="lg" />
            <v-list-item prepend-icon="mdi-cog-outline" title="Configurações" rounded="lg" />
            <v-divider class="my-1" />
            <v-list-item
              prepend-icon="mdi-logout"
              title="Sair"
              rounded="lg"
              base-color="error"
              @click="logout"
            />
          </v-list>

        </v-card>
      </v-menu>

    </v-app-bar>


    <!-- ═══════════════════════════════════════════════
         NAVIGATION DRAWER
    ═══════════════════════════════════════════════ -->
    <v-navigation-drawer
      v-model="drawer"
      :rail="mini"
      :temporary="isMobile"
      width="320"
      class="drawer"
      role="navigation"
      aria-label="Menu lateral de navegação"
    >

      <!-- Header do Drawer -->
      <div class="drawer-header">
        <v-icon size="24" color="#1565c0">mdi-view-dashboard</v-icon>

        <Transition name="fade">
          <span v-if="!mini" class="drawer-title">TABOSA RELATÓRIOS</span>
        </Transition>

        <v-spacer />

        <v-btn
          icon
          size="small"
          variant="text"
          :aria-label="mini ? 'Expandir menu' : 'Recolher menu'"
          @click="mini = !mini"
        >
          <v-icon size="18">{{ mini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <!-- Campo de Busca -->
      <Transition name="fade">
        <v-text-field
          v-if="!mini"
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Buscar no menu..."
          density="compact"
          variant="solo-filled"
          flat
          clearable
          hide-details
          class="menu-search"
          aria-label="Buscar itens do menu"
        />
      </Transition>

      <!-- Lista de Menus -->
      <v-list density="compact" nav class="menu-list">
        <v-list-group
          v-for="grupo in menusFiltrados"
          :key="grupo.titulo"
          :value="grupo.titulo"
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :prepend-icon="grupo.icon"
              :title="grupo.titulo"
              rounded="lg"
              class="group-activator"
              @click="handleGroupClick(grupo)"
            >
              <!-- Tooltip para o grupo quando está recolhido -->
              <v-tooltip v-if="mini" activator="parent" location="right">
                {{ grupo.titulo }}
              </v-tooltip>
            </v-list-item>
          </template>

          <!-- Itens do submenu - só mostrar se não estiver recolhido -->
          <template v-if="!mini">
            <v-list-item
              v-for="item in grupo.items"
              :key="item.rota"
              :to="item.rota"
              :prepend-icon="item.icon"
              :title="item.titulo"
              rounded="lg"
              class="menu-item"
            />
          </template>

        </v-list-group>
      </v-list>

      <!-- Footer do Drawer -->
      <template #append>
        <div class="drawer-footer">
          <v-divider />

          <Transition name="fade">
            <p v-if="!mini" class="version-text">{{ version }} | {{ buildDate }}</p>
          </Transition>

          <v-btn
            block
            variant="tonal"
            color="error"
            class="logout-btn"
            :icon="mini"
            @click="logout"
            aria-label="Sair do sistema"
          >
            <v-icon :start="!mini">mdi-logout</v-icon>
            <span v-if="!mini">Sair</span>
          </v-btn>
        </div>
      </template>

    </v-navigation-drawer>


    <!-- ═══════════════════════════════════════════════
         CONTEÚDO PRINCIPAL
    ═══════════════════════════════════════════════ -->
    <v-main class="main-content">
      <router-view v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" />
        </Transition>
      </router-view>
    </v-main>

  </v-app>
</template>


<script setup>
import { ref, computed, inject, watch, onMounted } from 'vue'
import { useRouter, useRoute }                     from 'vue-router'
import { useDisplay }                              from 'vuetify'
import { getVersionInfo }                          from '../config/version'

defineOptions({ name: 'AppLayout' })

// ── Injeções do App.vue (provide/inject) ────────────────────
const showSnackbar = inject('showSnackbar', null)
const setLoading   = inject('setLoading',   null)

// ── Vuetify / Router / Route ─────────────────────────────────
const { mobile } = useDisplay()
const router     = useRouter()
const route      = useRoute()

// ── Estado Reativo ───────────────────────────────────────────
const drawer            = ref(true)
const mini              = ref(false)
const search            = ref('')
const notificationCount = ref(3)
const { version, buildDate } = getVersionInfo()

const currentUser = ref({
  name:     'Administrador',
  role:     'Gestor de Relatórios',
  email:    'admin@tabosa.com',
  initials: 'AD'
})

// ── Estrutura dos Menus ──────────────────────────────────────
const menus = ref([
  {
    titulo: 'Relatórios',
    icon: 'mdi-file-chart',
    items: [
      { titulo: 'Relatório Expediente', icon: 'mdi-format-list-bulleted', rota: '/menu/relatorios' },
    ]
  },
  {
    titulo: 'Dashboard',
    icon: 'mdi-view-dashboard',
    items: [
      { titulo: 'Painel Principal', icon: 'mdi-chart-line', rota: '/menu/dashboard' }
    ]
  },
/*
  {
    titulo: 'Cadastros',
    icon: 'mdi-database-outline',
    items: [
      { titulo: 'Produtos', icon: 'mdi-package-variant-closed', rota: '/menu/produtos' },
      { titulo: 'Clientes', icon: 'mdi-account-group-outline',  rota: '/menu/clientes' }
    ]
  },*/
  {
    titulo: 'Configurações',
    icon: 'mdi-cog-outline',
    items: [
      { titulo: 'Sistema', icon: 'mdi-tune', rota: '/menu/configuracoes' },
      { titulo: 'Logs do Sistema', icon: 'mdi-console', rota: '/menu/sistema-logs', adminOnly: true },
      { titulo: 'Logs de Acesso', icon: 'mdi-login', rota: '/menu/access-logs', adminOnly: true }
    ]
  }
])

// ── Computed ─────────────────────────────────────────────────
/** Filtra os grupos e itens conforme o texto buscado */
const menusFiltrados = computed(() => {
  if (!search.value) return menus.value

  const termo = search.value.toLowerCase().trim()
  const userName = localStorage.getItem('user_name') || ''
  const isAdmin = localStorage.getItem('is_admin') === 'true'
  const isTabosa = userName === 'tabosa'

  return menus.value
    .map(grupo => ({
      ...grupo,
      items: grupo.items.filter(item => {
        const matchesSearch = item.titulo.toLowerCase().includes(termo)
        const isAdminAllowed = !item.adminOnly || (isAdmin || isTabosa)
        return matchesSearch && isAdminAllowed
      })
    }))
    .filter(grupo => grupo.items.length > 0)
})

/** Drawer temporário em telas pequenas */
const isMobile = computed(() => mobile.value)

// ── Watchers ─────────────────────────────────────────────────
watch(isMobile, (isMob) => {
  if (isMob) {
    mini.value   = false    // sem rail no mobile
    drawer.value = false    // começa fechado no mobile
  } else {
    drawer.value = true     // sempre visível no desktop
    mini.value   = false    // expandido por padrão
  }
}, { immediate: true })    // ← roda na montagem também

// ── Lifecycle ────────────────────────────────────────────────
onMounted(() => {
  if (window.innerWidth < 960) {
    drawer.value = false
  }
  
  // Registrar acesso à página
  trackPageAccess()
})

// ── Functions ────────────────────────────────────────────────
const trackPageAccess = async () => {
  try {
    // Registrar acesso à página atual
    await axios.post('/api/track-page-access.php', {
      page: window.location.pathname
    })
  } catch (error) {
    console.log('Page tracking failed:', error)
  }
}

// ── Métodos ──────────────────────────────────────────────────
/**
 * Realiza o logout do usuário com feedback visual.
 */


 
 // ── Método: toggle inteligente do drawer ─────────────────────
/**
 * No desktop  → alterna entre modo rail (mini) e expandido
 * No mobile   → mostra/esconde o drawer por completo
 */
function toggleDrawer() {
  if (isMobile.value) {
    drawer.value = !drawer.value   // mobile: abre/fecha
  } else {
    mini.value = !mini.value       // desktop: expande/recolhe (rail)
    drawer.value = true            // garante que nunca fecha no desktop
  }
}

/**
 * Expande o menu quando clicar em um grupo no modo recolhido
 */
function handleGroupClick(grupo) {
  if (mini.value) {
    // Se estiver recolhido, expande o menu
    mini.value = false
    drawer.value = true
  }
}

 
async function logout() {
  try {
    setLoading?.(true, 'Saindo do sistema...')
    await fetch('/auth/logout.php', { method: 'POST' })
    await router.push('/login')
  } catch (err) {
    console.error('[AppLayout] Erro no logout:', err)
    showSnackbar?.('Erro ao sair. Tente novamente.', 'error')
  } finally {
    setLoading?.(false)
  }
}
</script>


<style scoped>
/* ── APP BAR ──────────────────────────────────────────────── */
.app-bar {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 65%, #42a5f5 100%) !important;
  box-shadow: 0 2px 12px rgba(21, 101, 192, 0.3) !important;
}

.app-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-brand-text {
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 1.2px;
  color: #fff;
}

.user-avatar {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.user-initials {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}

/* ── USER MENU DROPDOWN ────────────────────────────────────── */
.user-menu-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 12px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%);
  text-align: center;
}

.text-heading { color: #1e293b; }
.text-muted   { color: #94a3b8; }

/* ── DRAWER ───────────────────────────────────────────────── */
.drawer {
  background: #ffffff !important;
  border-right: 1px solid #e8edf5 !important;
}

/* ── DRAWER HEADER ────────────────────────────────────────── */
.drawer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px;
  background: linear-gradient(135deg, #f8faff 0%, #eef3ff 100%);
  border-bottom: 1px solid #e8edf5;
  min-height: 56px;
}

.drawer-title {
  font-weight: 700;
  font-size: 12.5px;
  letter-spacing: 1px;
  color: #1565c0;
  white-space: nowrap;
}

/* ── BUSCA ────────────────────────────────────────────────── */
.menu-search {
  margin: 10px 10px 6px;
}

.menu-search :deep(.v-field) {
  background: #f0f4ff !important;
  border-radius: 10px !important;
  font-size: 13px;
}

/* ── LISTA ────────────────────────────────────────────────── */
.menu-list {
  padding: 6px 0;
}

/* ── GROUP ACTIVATOR ──────────────────────────────────────── */
.group-activator {
  font-weight: 700 !important;
  font-size: 12.5px !important;
  letter-spacing: 0.3px;
  color: #1e293b !important;
  margin: 1px 6px;
}

.group-activator :deep(.v-list-item__prepend .v-icon) {
  color: #1565c0 !important;
  opacity: 1 !important;
}

/* ── ITENS DO MENU ────────────────────────────────────────── */
.menu-item {
  margin: 2px 8px !important;
  font-size: 13.5px !important;
  border-radius: 10px !important;
  transition: background .2s ease, transform .18s ease;
  color: #475569;
}

.menu-item:hover {
  background: rgba(21, 101, 192, 0.07) !important;
  transform: translateX(3px);
}

/* Estado Ativo (rota atual) */
.menu-item.v-list-item--active,
.menu-item:deep(.v-list-item--active) {
  background: linear-gradient(
    90deg,
    rgba(21, 101, 192, 0.13) 0%,
    rgba(21, 101, 192, 0.04) 100%
  ) !important;
  border-left: 3px solid #1565c0 !important;
  color: #1565c0 !important;
  font-weight: 600 !important;
}

.menu-item.v-list-item--active :deep(.v-icon) {
  color: #1565c0 !important;
}

/* ── FOOTER DO DRAWER ─────────────────────────────────────── */
.drawer-footer {
  padding: 8px 10px 14px;
}

.version-text {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  margin: 6px 0 8px;
  letter-spacing: 0.5px;
}

.logout-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
}

/* ── CONTEÚDO PRINCIPAL ───────────────────────────────────── */
.main-content {
  background: #f0f4f8 !important;
  min-height: 100vh;
}

/* ── TRANSIÇÃO DE PÁGINAS ─────────────────────────────────── */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease, transform 0.22s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-leave-to {
  opacity: 0;
}

/* ── TRANSIÇÃO FADE (drawer title / search) ───────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
```__